import React, { useMemo } from 'react';
import { PencilIcon, PlusIcon } from 'lucide-react';
import type { Milestone } from '../types/life';
import { cn } from '../utils/cn';
import { formatCountdown, formatElapsed } from '../utils/format';
import { milestoneStart, milestoneUnitLabel } from '../utils/lifeGrid';
import { useNow } from '../hooks/useNow';
interface MilestonePanelProps {
  milestones: Milestone[];
  onAdd: () => void;
  onEdit: (milestone: Milestone) => void;
  onFocus: (milestone: Milestone) => void;
}
interface RowProps {
  milestone: Milestone;
  now: Date;
  ahead: boolean;
  onEdit: (milestone: Milestone) => void;
  onFocus: (milestone: Milestone) => void;
}
function MilestoneRow({
  milestone,
  now,
  ahead,
  onEdit,
  onFocus
}: RowProps) {
  const start = milestoneStart(milestone);
  return <li className="group flex items-baseline gap-3 py-1.5" data-id="element-37" data-mp="3r0gdmee9tx9g">
      <button type="button" onClick={() => onFocus(milestone)} className="min-w-0 flex-1 text-left" aria-label={`Show "${milestone.title}" in the grid`} data-id="element-38" data-mp="3r214nu1dmy2k">
        <span className={cn('block truncate text-sm font-medium transition-colors duration-150 ease-swift', ahead ? 'text-ink group-hover:text-accent' : 'text-muted line-through decoration-1')} data-id="element-39" data-mp="3glc6rziz1bio">
          {milestone.title}
        </span>
        <span className="text-muted mt-0.5 block truncate font-mono text-[11px]" data-id="element-40" data-mp="3bkev85sgvfq5">
          {milestoneUnitLabel(milestone)}
        </span>
      </button>
      <span className={cn('shrink-0 font-mono text-xs tabular-nums', ahead ? 'text-accent' : 'text-muted')} data-id="element-41" data-mp="32krpx8wm9prt">
        {ahead ? formatCountdown(start, now) : formatElapsed(start, now)}
      </span>
      <button type="button" onClick={() => onEdit(milestone)} aria-label={`Edit "${milestone.title}"`} className="text-muted hover:text-ink shrink-0 opacity-0 transition-opacity duration-150 ease-swift focus-visible:opacity-100 group-hover:opacity-100" data-id="element-42" data-mp="2suyq155u6z3e">
        <PencilIcon className="h-3.5 w-3.5" data-id="element-43" data-mp="pebm0tk2iaqa" />
      </button>
    </li>;
}
export function MilestonePanel({
  milestones,
  onAdd,
  onEdit,
  onFocus
}: MilestonePanelProps) {
  const now = useNow(1000);
  const {
    ahead,
    behind
  } = useMemo(() => {
    const withStart = milestones.map(m => ({
      m,
      start: milestoneStart(m)
    }));
    return {
      ahead: withStart.filter(x => x.start.getTime() > now.getTime()).sort((a, b) => a.start.getTime() - b.start.getTime()).map(x => x.m),
      behind: withStart.filter(x => x.start.getTime() <= now.getTime()).sort((a, b) => b.start.getTime() - a.start.getTime()).map(x => x.m)
    };
  }, [milestones, now]);
  return <section className="border-line bg-paper shrink-0 border-t px-4 py-3 sm:px-6 sm:py-4" aria-label="Milestones" data-id="element-44" data-mp="udxxoyk8ziwz">
      <div className="flex items-center justify-between gap-4" data-id="element-45" data-mp="3j60gbug50sjt">
        <h2 className="text-ink text-sm font-semibold tracking-tight" data-id="element-46" data-mp="3f346as4ny4rh">Milestones</h2>
        <button type="button" onClick={onAdd} className="text-muted hover:text-ink flex items-center gap-1.5 text-xs font-medium transition-colors duration-150 ease-swift" data-id="element-47" data-mp="1ull4b5c0q9wj">
          <PlusIcon className="h-3.5 w-3.5" data-id="element-48" data-mp="23lrhsr7pv9vp" />
          Add
        </button>
      </div>

      <div className="mt-3 grid gap-x-8 gap-y-4 md:grid-cols-2" data-id="element-49" data-mp="28cpznv8csg12">
        <div data-id="element-50" data-mp="28eaxbtpysf35">
          <p className="text-muted mb-1 text-[11px]" data-id="element-51" data-mp="28euev9bpd1gn">Ahead</p>
          {ahead.length === 0 ? <p className="text-muted py-1.5 text-xs" data-id="element-52" data-mp="2defapavkstp5">
              Nothing waiting. Click any cell in the grid to put a point ahead of you.
            </p> : <ul className="divide-line thin-scroll max-h-[76px] divide-y overflow-y-auto pr-1 md:max-h-[124px]" data-id="element-53" data-mp="2mdall09kerys">
              {ahead.map(m => <MilestoneRow key={m.id} milestone={m} now={now} ahead onEdit={onEdit} onFocus={onFocus} data-id="element-54" data-mp="ryzvypl5btry" />)}
            </ul>}
        </div>

        <div className="border-line md:border-l md:pl-8" data-id="element-55" data-mp="wzxbeueru9pr">
          <p className="text-muted mb-1 text-[11px]" data-id="element-56" data-mp="1mmr1xj5drzb">Behind</p>
          {behind.length === 0 ? <p className="text-muted py-1.5 text-xs" data-id="element-57" data-mp="3ssxlvbxkdwi7">Nothing behind you yet.</p> : <ul className="divide-line thin-scroll max-h-[76px] divide-y overflow-y-auto pr-1 md:max-h-[124px]" data-id="element-58" data-mp="bkspg55qwfzf">
              {behind.map(m => <MilestoneRow key={m.id} milestone={m} now={now} ahead={false} onEdit={onEdit} onFocus={onFocus} data-id="element-59" data-mp="6kwn0acp4wlz" />)}
            </ul>}
        </div>
      </div>
    </section>;
}